import type { Identity } from '@cef-ai/wallet-identity';

export interface LoginGesture {
  /** Called when `login()` needs a user gesture before it can proceed. */
  onAwaitGesture: (proceed: () => void) => void;
}

/**
 * Wraps `identity` so `login()` first waits for an external gesture (a
 * "Continue" button click, in practice) before running the real ceremony.
 *
 * Why: a WebAuthn `get()` ceremony requires user activation. Inside a
 * cross-origin iframe, the `wallet:hello` message from the host arrives
 * asynchronously (postMessage), so calling `identity.login()` directly from
 * the message handler has no activation left to spend. Gating `login()`
 * behind a real click gives the ceremony a fresh activation event.
 *
 * Implementation: `Object.create(identity)` builds an object whose prototype
 * IS the real identity, so every member this wrapper does not explicitly
 * override (including `IdentityImpl`-only members like `getVault`, and any
 * future additions to the concrete class) passes straight through via the
 * prototype chain. A prototype-chain call forwards the caller's real arguments
 * by construction, so **the only member that can lose one is a hand-written
 * override, and `login` is the only override here** — that is the sole member
 * the gesture gate needs to intercept. `register` is intentionally left
 * alone: `EmbedWallet.register()` (SDK side, Task 6) always punches out to a
 * real popup window, so a `wallet:hello{intent:'register'}` never reaches
 * this iframe-gated path.
 *
 * **The override forwards `...args`, not a named parameter, and that is
 * load-bearing.** It first shipped as `() => identity.login()`, which silently
 * dropped the `{ credentialId }` pin that `createHelloHandler` passes on a
 * step-up. That pin is what keeps the WebAuthn `allowCredentials` list narrowed
 * to the session's own credential; without it the browser shows the
 * discoverable-credential picker and the reader can authenticate as a
 * DIFFERENT passkey. It was latent until S1 — before this release a step-up
 * punched out to a popup, whose identity is ungated, so no pinned `login()`
 * ever reached this wrapper. S1 routes the step-up through the frame and made
 * it live. Spreading the arguments means a future parameter cannot repeat it.
 */
export function createGatedIdentity(identity: Identity, gesture: LoginGesture): Identity {
  const gated = Object.create(identity) as Identity;
  gated.login = (...args: Parameters<Identity['login']>) =>
    new Promise<void>((resolve, reject) => {
      gesture.onAwaitGesture(() => {
        identity.login(...args).then(resolve, reject);
      });
    });
  return gated;
}
